// Staged upload cards resting inside the composer card, above the text
// input. Uploads happen when the file is picked, not on send, so each card
// carries its own uploading/failed state and a remove control.
import { FileText, Loader2, X } from 'lucide-react';
import { type ReactNode } from 'react';

import { type ChatInputImageAttachment } from '@apps/web/lib/chat_attachment_types';
import { cn } from '@apps/web/lib/utils';

function formatSize(sizeBytes: number): string {
    if (sizeBytes >= 1024 * 1024) {
        return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`;
    }
    return `${Math.max(1, Math.round(sizeBytes / 1024))} KB`;
}

export function ComposerAttachmentCards({
    attachments,
    disabled = false,
    onRemove,
}: Readonly<{
    readonly attachments: ReadonlyArray<ChatInputImageAttachment>;
    readonly disabled?: boolean;
    readonly onRemove: (attachmentId: string) => void;
}>): ReactNode {
    if (attachments.length === 0) {
        return null;
    }
    return (
        <div className="mb-2 flex flex-wrap items-center gap-1.5">
            {attachments.map((attachment) => (
                <div
                    key={attachment.id}
                    className={cn(
                        'bg-muted relative flex max-w-56 items-center gap-1.5 rounded-md border py-1 pr-6 pl-1.5',
                        attachment.status === 'failed' &&
                            'border-destructive/50',
                    )}
                    title={
                        attachment.status === 'failed'
                            ? (attachment.errorMessage ?? 'Upload failed.')
                            : (attachment.filename ?? undefined)
                    }
                >
                    {attachment.previewUrl !== null ? (
                        <img
                            src={attachment.previewUrl}
                            alt={attachment.filename ?? 'Attached image'}
                            className="size-7 shrink-0 rounded-sm object-cover"
                        />
                    ) : (
                        <span className="text-muted-foreground grid size-7 shrink-0 place-items-center">
                            <FileText
                                aria-hidden="true"
                                className="size-[17px]"
                            />
                        </span>
                    )}
                    <span className="min-w-0">
                        <span className="text-v3-chat-meta block max-w-40 truncate font-medium">
                            {attachment.filename ?? 'Attachment'}
                        </span>
                        <span
                            className={cn(
                                'block text-v3-chat-meta',
                                attachment.status === 'failed'
                                    ? 'text-destructive'
                                    : 'text-muted-foreground',
                            )}
                        >
                            {attachment.status === 'uploading'
                                ? 'Uploading…'
                                : attachment.status === 'failed'
                                  ? 'Upload failed'
                                  : formatSize(attachment.sizeBytes)}
                        </span>
                    </span>
                    {attachment.status === 'uploading' && (
                        <Loader2
                            aria-hidden="true"
                            className="text-muted-foreground size-3.5 shrink-0 animate-spin motion-reduce:animate-none"
                        />
                    )}
                    <button
                        type="button"
                        aria-label={`Remove ${attachment.filename ?? 'attachment'}`}
                        disabled={disabled}
                        onClick={() => onRemove(attachment.id)}
                        className="text-muted-foreground hover:text-foreground focus-visible:outline-agent-action absolute top-1/2 right-1 grid size-4.5 -translate-y-1/2 place-items-center rounded-md outline-none focus-visible:outline-2 disabled:opacity-[0.42]"
                    >
                        <X aria-hidden="true" className="size-3" />
                    </button>
                </div>
            ))}
        </div>
    );
}
